import React from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import {
  Home,
  Users,
  DollarSign,
  ClipboardList,
  Shield,
  BarChart2,
  FileText,
  TestTube,
  UsersIcon,
  TrendingUp,
  CheckCircle,
  Code,
  Target,
  BookOpen,
  SlidersHorizontal,
  Settings2,
  Inbox,
  KeyRound,
  FileCheck,
  FileOutput,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../constants/featureFlags';

const Sidebar: React.FC = () => {
  const navigateTo = useNavigateWithNewTab();
  const location = useLocation();
  const {
    user,
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
    isSidebarCollapsed,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      isSidebarCollapsed: state.isSidebarCollapsed,
    }))
  );

  // Helper to check if a route is active
  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <aside
      className={`${isSidebarCollapsed ? 'w-16' : 'w-52'} border-r bg-background flex flex-col h-screen transition-all duration-300 relative`}
    >
      {/* Header */}
      <div className='flex items-center gap-2 px-2 py-2 border-b'>
        <div className='h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white text-2xl font-bold shrink-0'>
          R
        </div>
        <div
          className={`flex-1 transition-opacity duration-300 ${isSidebarCollapsed ? 'opacity-0' : 'opacity-100'}`}
        >
          <div className='font-semibold whitespace-nowrap'>Roger</div>
          <div className='text-muted-foreground text-sm whitespace-nowrap'>
            {user?.agency?.written_name || '[Agency Name]'}
          </div>
        </div>
      </div>

      {/* Menu */}
      <nav className='flex-1 px-2 py-3 space-y-1'>
        {/* RogerDoc Dashboard Section - requires rogerdoc-dashboard:manage */}
        {hasPermission('rogerdoc-dashboard:manage') && (
          <>
            <button
              onClick={navigateTo('/rogerdoc/overview')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-medium ${isActive('/rogerdoc/overview') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
            >
              <BarChart2 className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Overview</span>
              )}
            </button>
            <button
              onClick={navigateTo('/rogerdoc/users')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-mediumm ${isActive('/rogerdoc/users') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
            >
              <UsersIcon className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Users</span>
              )}
            </button>
            <button
              onClick={navigateTo('/rogerdoc/usage')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-medium ${isActive('/rogerdoc/usage') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
            >
              <TrendingUp className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Usage</span>
              )}
            </button>
          </>
        )}

        {/* Customizations - requires customizations:access permission */}
        {hasPermission('customizations:access') && (
          <button
            onClick={navigateTo('/customizations')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-medium ${isActive('/customizations') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
          >
            <SlidersHorizontal className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Customizations</span>
            )}
          </button>
        )}

        {/* Form Schemas - requires emr:manage permission */}
        {hasPermission('emr:manage') && (
          <button
            onClick={navigateTo('/form-schemas')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-medium ${isActive('/form-schemas') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
          >
            <FileText className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Form Schemas</span>
            )}
          </button>
        )}

        {/* Resources - visible to all authenticated users */}
        <button
          onClick={navigateTo('/resources')}
          className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 text-medium ${isActive('/resources') ? 'bg-accent/50 text-primary font-medium' : 'hover:bg-accent/30 text-foreground'}`}
        >
          <BookOpen className='h-4 w-4' />
          {!isSidebarCollapsed && (
            <span className='text-sm font-normal'>Resources</span>
          )}
        </button>

        {/* Horizontal line under RogerDoc / Resources section */}
        {!isSidebarCollapsed && <div className='w-full h-px bg-border my-2' />}

        {/* Old QA Section - requires qa-legacy:access */}
        {hasPermission('qa-legacy:access') && (
          <button
            onClick={navigateTo('/qa-legacy')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/qa-legacy') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <CheckCircle className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Quality Assurance</span>
            )}
          </button>
        )}

        {/* QA Section - requires qa:access */}
        {hasPermission('qa:access') && (
          <button
            onClick={navigateTo('/qa')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/qa') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <CheckCircle className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Quality Assurance</span>
            )}
          </button>
        )}

        {/* Hotbox - task work queue for clinicians and case managers */}
        {hasAnyPermission(['hotbox:access', 'emr:manage']) && (
          <button
            onClick={navigateTo('/hotbox')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/hotbox') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <Inbox className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Hotbox</span>
            )}
          </button>
        )}

        {/* Medical Coding Section - requires coding:access */}
        {hasPermission('coding:access') && (
          <>
            <button
              onClick={navigateTo('/coding')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/coding') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Code className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Medical Coding</span>
              )}
            </button>
          </>
        )}

        {hasFeatureFlag(FeatureFlag.ADMIN_WORKSHEET_GOALS) && (
          <>
            <button
              onClick={navigateTo('/worksheet-goals')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/worksheet-goals') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Target className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Worksheet Goals</span>
              )}
            </button>
          </>
        )}

        {/* Reporting */}
        {hasPermission('reports:access') && (
          <>
            <button
              onClick={navigateTo('/reporting')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/reporting') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <BarChart2 className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Reporting</span>
              )}
            </button>
          </>
        )}

        {/* Intake Manager - requires intake:access */}
        {hasPermission('intake:access') && (
          <button
            onClick={navigateTo('/intake-manager')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/intake-manager') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <FileText className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Intake Manager</span>
            )}
          </button>
        )}

        {/* NOA Manager - requires noa-manager feature flag */}
        {hasFeatureFlag(FeatureFlag.NOA_MANAGER) && (
          <button
            onClick={navigateTo('/noa-manager')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/noa-manager') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <FileCheck className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>NOA Manager</span>
            )}
          </button>
        )}

        {/* OASIS Manager - requires oasis-manager feature flag */}
        {hasFeatureFlag(FeatureFlag.OASIS_MANAGER) && (
          <button
            onClick={navigateTo('/oasis-manager')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/oasis-manager') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <FileOutput className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>OASIS Manager</span>
            )}
          </button>
        )}

        {/* Clinician Manager - requires clinician-mgr:access OR emr:manage */}
        {hasAnyPermission(['clinician-mgr:access', 'emr:manage']) && (
          <button
            onClick={navigateTo('/clinician-manager')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/clinician-manager') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <Settings2 className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Clinician Manager</span>
            )}
          </button>
        )}

        <>
          {/* Dashboard - requires emr:manage */}
          {hasPermission('emr:manage') && (
            <button
              onClick={navigateTo('/dashboard')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/dashboard') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Home className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Dashboard</span>
              )}
            </button>
          )}
          {/* Patient Manager - requires patient-mgr:access OR emr:manage */}
          {hasAnyPermission(['patient-mgr:access', 'emr:manage']) && (
            <button
              onClick={navigateTo('/patients')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/patients') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Users className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Patient Manager</span>
              )}
            </button>
          )}
          {/* Billing - requires emr:manage */}
          {hasPermission('emr:manage') && (
            <button
              onClick={navigateTo('/billing')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/billing') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <DollarSign className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Billing</span>
              )}
            </button>
          )}
          {/* Orders Manager - requires emr:manage + feature flag */}
          {hasPermission('emr:manage') &&
            hasFeatureFlag(FeatureFlag.ORDER_MANAGER) && (
              <button
                onClick={navigateTo('/orders')}
                className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/orders') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
              >
                <ClipboardList className='h-4 w-4' />
                {!isSidebarCollapsed && (
                  <span className='text-sm font-normal'>Orders Manager</span>
                )}
              </button>
            )}
          {/* Auth Manager - requires emr:manage */}
          {hasPermission('emr:manage') && (
            <button
              onClick={navigateTo('/auth-manager')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/auth') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Shield className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Auth Manager</span>
              )}
            </button>
          )}
          {/* Internal Admin - requires emr:manage */}
          {hasPermission('emr:manage') && (
            <button
              onClick={navigateTo('/internal-admin')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/internal-admin') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <Shield className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Internal Admin</span>
              )}
            </button>
          )}
          {/* Testing - requires emr:manage */}
          {hasPermission('emr:manage') && (
            <button
              onClick={navigateTo('/testing')}
              className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/testing') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
            >
              <TestTube className='h-4 w-4' />
              {!isSidebarCollapsed && (
                <span className='text-sm font-normal'>Testing</span>
              )}
            </button>
          )}
        </>

        {/* Admin - requires external-admin:access OR emr:manage */}
        {hasAnyPermission(['external-admin:access', 'emr:manage']) && (
          <button
            onClick={navigateTo('/admin')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/admin') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <Shield className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Admin</span>
            )}
          </button>
        )}

        {/* Roles & Permissions - requires roger-rbac feature flag AND external-admin:access OR emr:manage */}
        {hasAnyPermission(['admin:super-user']) && (
          <button
            onClick={navigateTo('/roles-manager')}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2 font-medium ${isActive('/roles-manager') ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'} mb-1`}
          >
            <KeyRound className='h-4 w-4' />
            {!isSidebarCollapsed && (
              <span className='text-sm font-normal'>Roles & Permissions</span>
            )}
          </button>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
